import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { FullScreenProgress } from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { useOpenCommerceUserUpdateMutation } from '@occc-app/dataAccessLayer/openCommerceUser/mutations';
import {
  RetailerNetworkUserFragment,
  useRemoveUserFromRetailerNetworkMutation,
} from '@occc/graphql';

import { RetailerNetworkUserUpdateFormValues } from './components/retailer-network-user-update-dialog/retailer-network-user-update-dialog.types';
import { RetailerNetworkUserDetailsPage } from './retailer-network-user-details-page.component';

import { useGetRetailerNetworkUsers } from '../../data-access/queries';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';

export const RetailerNetworkUserDetailsPageContainer = () => {
  const { networkId = '', userId = '' } = useParams<{
    networkId: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const { data, loading, error } = useGetRetailerNetworkUsers({
    filter: {
      retailerNetworkUuidEquals: networkId,
      userUuidEquals: userId,
    },
  });
  const [removeUserFromRetailerNetworkDialogOpen, setRemoveUserFromRetailerNetworkDialogOpen] =
    useState<boolean>(false);

  const retailerNetworkUser: RetailerNetworkUserFragment | undefined = data?.[0];

  const breadcrumbLinks = useMemo(
    () => [{ label: retailerNetworkUser?.name || 'User Details' }],
    [retailerNetworkUser?.name],
  );

  useBreadcrumbLinks(breadcrumbLinks);

  const [isOpenCommerceUserUpdateDialogOpen, setIsOpenCommerceUserUpdateDialogOpen] =
    useState(false);
  const [openCommerceUserUpdate] = useOpenCommerceUserUpdateMutation({
    onCompleted: () => snackbar.success('User data has been successfully updated'),
    refetchQueries: ['retailerNetworkUsers'],
  });

  const handleOpenCommerceUserUpdateDialogOpen = useCallback(() => {
    setIsOpenCommerceUserUpdateDialogOpen(!isOpenCommerceUserUpdateDialogOpen);
  }, [isOpenCommerceUserUpdateDialogOpen]);

  const handleRemoveUserFromRetailerNetwork = () => {
    setRemoveUserFromRetailerNetworkDialogOpen(true);
  };

  const [removeUserFromRetailerNetwork, { loading: isRemoveUserFromRetailerNetworkInProgress }] =
    useRemoveUserFromRetailerNetworkMutation({
      onCompleted: () => {
        navigate(routeCreator.details(networkId));
        snackbar.success('User has been successfully removed from retailer network');
      },
      refetchQueries: ['retailerNetworkUsers'],
    });

  const onRemoveUserFromRetailerNetwork = useCallback(async () => {
    const openCommerceUserUuid = retailerNetworkUser?.openCommerceUser?.uuid;

    if (!openCommerceUserUuid) {
      return;
    }

    await removeUserFromRetailerNetwork({
      variables: {
        retailerNetworkUuid: networkId,
        userUuid: openCommerceUserUuid,
      },
    });

    setRemoveUserFromRetailerNetworkDialogOpen(false);
  }, [networkId, removeUserFromRetailerNetwork, retailerNetworkUser?.openCommerceUser?.uuid]);

  const handleOpenCommerceUserUpdate = useCallback(
    async (values: RetailerNetworkUserUpdateFormValues) => {
      if (!retailerNetworkUser?.userUuid) {
        return;
      }
      await openCommerceUserUpdate({
        variables: {
          input: {
            uuid: retailerNetworkUser?.userUuid,
            name: {
              first: values.firstName,
              last: values.lastName,
            },
            email: values.email,
            phoneNumber: values.phoneNumber || null,
          },
        },
      });
      handleOpenCommerceUserUpdateDialogOpen();
    },
    [handleOpenCommerceUserUpdateDialogOpen, openCommerceUserUpdate, retailerNetworkUser?.userUuid],
  );

  if (loading && !data) {
    return <FullScreenProgress />;
  }

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!retailerNetworkUser) {
    return <RetailerNetworkNoData />;
  }

  return (
    <RetailerNetworkUserDetailsPage
      retailerNetworkUuid={networkId}
      retailerNetworkUser={retailerNetworkUser}
      removeUserFromRetailerNetworkDialogOpen={removeUserFromRetailerNetworkDialogOpen}
      isRemoveUserFromRetailerNetworkInProgress={isRemoveUserFromRetailerNetworkInProgress}
      isOpenCommerceUserUpdateDialogOpen={isOpenCommerceUserUpdateDialogOpen}
      handleOpenCommerceUserUpdateDialogOpen={handleOpenCommerceUserUpdateDialogOpen}
      handleRemoveUserFromRetailerNetwork={handleRemoveUserFromRetailerNetwork}
      setRemoveUserFromRetailerNetworkDialogOpen={setRemoveUserFromRetailerNetworkDialogOpen}
      onRemoveUserFromRetailerNetworkConfirmPress={onRemoveUserFromRetailerNetwork}
      handleOpenCommerceUserUpdate={handleOpenCommerceUserUpdate}
    />
  );
};
